import { Box } from '@mui/material';
import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getBuildingAction } from 'store/actions/propertyActions';
import { openDetails } from 'store/actions/uiActions';
import { BuildingTable } from 'components/Tables/BuildingTable';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dropdown, Loading, Button } from '@leapeasy/ui-kit';
import { failedNotif } from 'store/actions/notifActions';
import { getBuilding } from 'store/sagas/APIs/property';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { usePaginatedListFetch } from 'hooks/usePaginatedListFetch';
import { useLazyExportDatasetTotal } from 'hooks/useLazyExportDatasetTotal';
import { fetchAllPaginated } from 'utils/exportPagination';
import { ApplicationListActiveFilters } from 'components/ApplicationListActiveFilters/ApplicationListActiveFilters';
import {
  buildPropertyListFilterChips,
  parsePropertyListFilterChipId,
} from 'utils/propertyListActiveFilterChips';
import { GlobalUXState } from 'components/GlobalUXState';
import { buildDetailNavigationState } from 'utils/detailBackNavigation';
import { BOOLEAN_YES_NO_OPTIONS } from 'utils/formatBooleanYesNo';

const parseBuildingsFilters = (searchParams, _paramKey, fallback) => {
  const propertyTypeParam = searchParams.get('propertyType') || searchParams.get('buildingType');
  return {
    selectedBType: propertyTypeParam
      ? propertyTypeParam.split(',').filter(Boolean)
      : fallback.selectedBType,
    isSHouse: searchParams.get('studentHousing')
      ? searchParams.get('studentHousing').split(',').filter(Boolean)
      : fallback.isSHouse,
    isDispositioned: searchParams.get('dispositioned')
      ? searchParams.get('dispositioned').split(',').filter(Boolean)
      : fallback.isDispositioned,
    unitFilter: searchParams.get('unitFilter') || fallback.unitFilter,
  };
};

const serializeBuildingsFilters = (filters) => ({
  propertyType:
    Array.isArray(filters.selectedBType) && filters.selectedBType.length > 0
      ? filters.selectedBType.join(',')
      : '',
  studentHousing:
    Array.isArray(filters.isSHouse) && filters.isSHouse.length > 0
      ? filters.isSHouse.join(',')
      : '',
  dispositioned:
    Array.isArray(filters.isDispositioned) && filters.isDispositioned.length > 0
      ? filters.isDispositioned.join(',')
      : '',
  unitFilter: filters.unitFilter || '',
});

const areArraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const normalizeSelectedBType = (value) => (Array.isArray(value) ? value : []);
const normalizeIsSHouse = (value) => (Array.isArray(value) ? value : []);
const normalizeIsDispositioned = (value) => (Array.isArray(value) ? value : []);
const normalizeUnitFilter = (value) => (value === 'active' || value === 'inactive' ? value : null);

const EMPTY_BUILDING_EXPORT_QUERY = {
  filter: { searchText: '', options: {} },
  selectedBType: [],
  isSHouse: [],
  isDispositioned: [],
  unitFilter: null,
};

const fetchBuildingExportDatasetTotal = async (sortOptions) => {
  const response = await getBuilding({
    ...EMPTY_BUILDING_EXPORT_QUERY,
    offset: 0,
    limit: 1,
    sortOptions,
  });
  return response?.total != null ? Number(response.total) : undefined;
};

const BuildingDropFilter = ({
  selectedBTypeDraft,
  setSelectedBTypeDraft,
  isSHouseDraft,
  setIsSHouseDraft,
  isDispositionedDraft,
  setIsDispositionedDraft,
  selectedBTypeApplied,
  isSHouseApplied,
  isDispositionedApplied,
  showDispositionedFilter,
  syncDraftWithApplied,
}) => {
  useEffect(() => {
    return () => {
      syncDraftWithApplied(selectedBTypeApplied, isSHouseApplied, isDispositionedApplied);
    };
  }, [selectedBTypeApplied, isSHouseApplied, isDispositionedApplied, syncDraftWithApplied]);

  return (
    <DropFilterContainer>
      <Dropdown
        onChange={(e) => {
          setSelectedBTypeDraft(e.target.value);
        }}
        options={[
          {
            text: 'Guarantor Waiver+LDR',
            value: 'Event Process;Auto Enroll',
          },
          {
            text: 'Rent Guaranty',
            value: 'Auto Enroll',
          },
        ]}
        value={selectedBTypeDraft}
        multiselect={true}
        showSearch={false}
        placeholder="Property Type"
        label="Property Type"
        style={{
          width: '100%',
        }}
      />

      <Dropdown
        onChange={(e) => {
          setIsSHouseDraft(e.target.value);
        }}
        options={BOOLEAN_YES_NO_OPTIONS}
        value={isSHouseDraft}
        multiselect={true}
        showSearch={false}
        placeholder="Student Housing"
        label="Student Housing"
        style={{
          width: '100%',
        }}
      />

      {showDispositionedFilter && (
        <Dropdown
          onChange={(e) => {
            setIsDispositionedDraft(e.target.value);
          }}
          options={BOOLEAN_YES_NO_OPTIONS}
          value={isDispositionedDraft}
          multiselect={true}
          showSearch={false}
          placeholder="Dispositioned"
          label="Dispositioned"
          style={{
            width: '100%',
          }}
        />
      )}
    </DropFilterContainer>
  );
};

export const Buildings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: 'Search Property..',
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: {
      selectedBType: [],
      isSHouse: [],
      isDispositioned: [],
      unitFilter: null,
    },
    parseExtraFilters: parseBuildingsFilters,
    serializeExtraFilters: serializeBuildingsFilters,
  });
  const [selectedBType, setSelectedBType] = useState(() =>
    normalizeSelectedBType(extraFilters?.selectedBType),
  );
  const [isSHouse, setIsSHouse] = useState(() => normalizeIsSHouse(extraFilters?.isSHouse));
  const [selectedBTypeDraft, setSelectedBTypeDraft] = useState(() =>
    normalizeSelectedBType(extraFilters?.selectedBType),
  );
  const [isSHouseDraft, setIsSHouseDraft] = useState(() =>
    normalizeIsSHouse(extraFilters?.isSHouse),
  );
  const [isDispositioned, setIsDispositioned] = useState(() =>
    normalizeIsDispositioned(extraFilters?.isDispositioned),
  );
  const [isDispositionedDraft, setIsDispositionedDraft] = useState(() =>
    normalizeIsDispositioned(extraFilters?.isDispositioned),
  );
  const [unitFilter, setUnitFilter] = useState(() => normalizeUnitFilter(extraFilters?.unitFilter));
  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedBType = normalizeSelectedBType(extraFilters.selectedBType);
    const nextIsSHouse = normalizeIsSHouse(extraFilters.isSHouse);
    const nextIsDispositioned = normalizeIsDispositioned(extraFilters.isDispositioned);
    const nextUnitFilter = normalizeUnitFilter(extraFilters.unitFilter);
    setSelectedBType((prev) =>
      areArraysEqual(prev, nextSelectedBType) ? prev : nextSelectedBType,
    );
    setSelectedBTypeDraft((prev) =>
      areArraysEqual(prev, nextSelectedBType) ? prev : nextSelectedBType,
    );
    setIsSHouse((prev) => (areArraysEqual(prev, nextIsSHouse) ? prev : nextIsSHouse));
    setIsSHouseDraft((prev) => (areArraysEqual(prev, nextIsSHouse) ? prev : nextIsSHouse));
    setIsDispositioned((prev) =>
      areArraysEqual(prev, nextIsDispositioned) ? prev : nextIsDispositioned,
    );
    setIsDispositionedDraft((prev) =>
      areArraysEqual(prev, nextIsDispositioned) ? prev : nextIsDispositioned,
    );
    setUnitFilter((prev) => (prev === nextUnitFilter ? prev : nextUnitFilter));
  }, [extraFilters]);

  const buildings = useSelector((state) => state.getIn(['property', 'building']));
  const buildingsJSON = buildings?.toJS?.() ?? null;
  const buildingListError = buildingsJSON?.buildingError;
  const currentUserState = useSelector((state) => state.getIn(['auth', 'currentUser']));
  const currentUserRole = currentUserState?.toJS?.()?.role;
  const showDispositionedFilter = currentUserRole !== 'pm';

  const listQuery = useMemo(
    () => ({
      filter,
      offset: pagination.pageNumber * pagination.rowsPerPage,
      limit: pagination.rowsPerPage,
      sortOptions,
      selectedBType,
      isSHouse,
      isDispositioned,
      unitFilter,
    }),
    [
      filter,
      pagination.pageNumber,
      pagination.rowsPerPage,
      sortOptions,
      selectedBType,
      isSHouse,
      isDispositioned,
      unitFilter,
    ],
  );

  usePaginatedListFetch({
    action: getBuildingAction,
    payload: listQuery,
  });

  const fetchFullExportDatasetTotal = useCallback(
    () => fetchBuildingExportDatasetTotal(sortOptions),
    [sortOptions],
  );

  const {
    fullExportDatasetTotal,
    isLoadingFullExportTotal,
    loadFullExportDatasetTotal,
    resetFullExportDatasetTotal,
  } = useLazyExportDatasetTotal(fetchFullExportDatasetTotal);

  useEffect(() => {
    resetFullExportDatasetTotal();
  }, [sortOptions, resetFullExportDatasetTotal]);

  const handleRetry = useCallback(() => {
    dispatch(getBuildingAction(listQuery));
  }, [dispatch, listQuery]);

  const handleRequestExportData = useCallback(async () => {
    const totalRecords = Number(buildingsJSON?.total) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getBuilding({
            filter,
            offset,
            limit,
            sortOptions,
            selectedBType,
            isSHouse,
            isDispositioned,
            unitFilter,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export the filtered properties right now.');
    }
  }, [
    buildingsJSON?.total,
    filter,
    sortOptions,
    selectedBType,
    isSHouse,
    isDispositioned,
    unitFilter,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    const totalRecords =
      Number(fullExportDatasetTotal) || Number(await loadFullExportDatasetTotal()) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getBuilding({
            ...EMPTY_BUILDING_EXPORT_QUERY,
            offset,
            limit,
            sortOptions,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export all properties right now.');
    }
  }, [fullExportDatasetTotal, loadFullExportDatasetTotal, sortOptions]);

  const syncDraftWithApplied = useCallback(
    (appliedSelectedBType, appliedIsSHouse, appliedIsDispositioned) => {
      setSelectedBTypeDraft(appliedSelectedBType);
      setIsSHouseDraft(appliedIsSHouse);
      setIsDispositionedDraft(appliedIsDispositioned);
    },
    [],
  );

  const onClickBuilding = useCallback(
    (building) => {
      const buildingId = building?.building_id;
      if (!buildingId) {
        dispatch(failedNotif('Property detail ID is missing.'));
        return;
      }
      dispatch(
        openDetails({
          type: 'property',
          data: building,
        }),
      );
      const returnTo = `${location.pathname}${location.search}`;
      navigate(`/property/properties/${buildingId}/detail`, {
        state: buildDetailNavigationState({}, returnTo),
      });
    },
    [dispatch, navigate, location.pathname, location.search],
  );
  const resetFunc = useCallback(() => {
    setSelectedBType([]);
    setIsSHouse([]);
    setIsDispositioned([]);
    setSelectedBTypeDraft([]);
    setIsSHouseDraft([]);
    setIsDispositionedDraft([]);
    setUnitFilter(null);
    setExtraFilters({
      selectedBType: [],
      isSHouse: [],
      isDispositioned: [],
      unitFilter: null,
    });
    setFilter((prev) => ({
      ...prev,
      searchText: '',
    }));
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setFilter, setPagination]);
  const applyFunc = useCallback(() => {
    setSelectedBType(selectedBTypeDraft);
    setIsSHouse(isSHouseDraft);
    setIsDispositioned(isDispositionedDraft);
    setExtraFilters({
      selectedBType: selectedBTypeDraft,
      isSHouse: isSHouseDraft,
      isDispositioned: isDispositionedDraft,
      unitFilter,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedBTypeDraft,
    isSHouseDraft,
    isDispositionedDraft,
    unitFilter,
    setExtraFilters,
    setPagination,
  ]);

  const listFilterChips = useMemo(
    () =>
      buildPropertyListFilterChips({
        selectedBType,
        isSHouse,
        isDispositioned,
        unitFilter,
      }),
    [selectedBType, isSHouse, isDispositioned, unitFilter],
  );

  const handleRemoveBuildingListFilterChip = useCallback(
    (chipId) => {
      const parsed = parsePropertyListFilterChipId(chipId);
      if (!parsed) return;

      if (parsed.type === 'unit') {
        setUnitFilter(null);
        setExtraFilters((prev) => ({ ...prev, unitFilter: null }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'propertyType') {
        const next = selectedBType.filter((s) => s !== parsed.value);
        setSelectedBType(next);
        setSelectedBTypeDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedBType: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'studentHousing') {
        const next = isSHouse.filter((s) => s !== parsed.value);
        setIsSHouse(next);
        setIsSHouseDraft(next);
        setExtraFilters((prev) => ({ ...prev, isSHouse: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'dispositioned') {
        const next = isDispositioned.filter((s) => s !== parsed.value);
        setIsDispositioned(next);
        setIsDispositionedDraft(next);
        setExtraFilters((prev) => ({ ...prev, isDispositioned: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      }
    },
    [selectedBType, isSHouse, isDispositioned, setExtraFilters, setPagination],
  );

  const listFilterActiveSummary = useMemo(() => {
    if (!listFilterChips.length) return null;
    return (
      <ApplicationListActiveFilters
        chips={listFilterChips}
        onRemoveChip={handleRemoveBuildingListFilterChip}
        onClearAll={resetFunc}
      />
    );
  }, [listFilterChips, handleRemoveBuildingListFilterChip, resetFunc]);
  const hasSearch = Boolean(`${filter?.searchText || ''}`.trim());
  const hasAppliedFilters = hasSearch || listFilterChips.length > 0;
  const buildingEmptyContent = hasAppliedFilters ? (
    <GlobalUXState
      variant="noResults"
      description="No properties match the current filters."
      compact
      actionLabel="Clear filters"
      onAction={resetFunc}
    />
  ) : (
    <GlobalUXState
      variant="noData"
      title="No properties available"
      description="There are no properties to display yet."
      compact
    />
  );

  const buildingTotal = Number(buildingsJSON?.total) || 0;

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: buildingsJSON?.total,
    isLoading: Boolean(buildingsJSON?.isLoading),
  });

  return (
    <DashboardLayoutContainer>
      {buildingsJSON ? (
        <React.Fragment>
          <CustomBox>
            {buildingListError && !buildingsJSON.isLoading ? (
              <Box display="flex" flexDirection="column" alignItems="center" gap="16px" mb={2}>
                <GlobalUXState variant="apiError" compact description={buildingListError} />
                <Button variant="primary" size="medium" onClick={handleRetry}>
                  Retry
                </Button>
              </Box>
            ) : null}
            <BuildingTable
              buildings={buildingsJSON.data ?? []}
              loading={buildingsJSON.isLoading}
              showEmptyState={!buildingsJSON.isLoading && !buildingListError}
              emptyContent={buildingEmptyContent}
              onRequestExportData={handleRequestExportData}
              onRequestFullExportData={handleRequestFullExportData}
              exportFullDatasetTotal={fullExportDatasetTotal}
              onLoadFullDatasetTotal={loadFullExportDatasetTotal}
              isLoadingFullDatasetTotal={isLoadingFullExportTotal}
              dropFilterActiveSummary={listFilterActiveSummary}
              dropFilterActiveCount={listFilterChips.length}
              filter={filter}
              onChangeFilter={setFilter}
              dropFilter={{
                component: (
                  <BuildingDropFilter
                    selectedBTypeDraft={selectedBTypeDraft}
                    setSelectedBTypeDraft={setSelectedBTypeDraft}
                    isSHouseDraft={isSHouseDraft}
                    setIsSHouseDraft={setIsSHouseDraft}
                    isDispositionedDraft={isDispositionedDraft}
                    setIsDispositionedDraft={setIsDispositionedDraft}
                    selectedBTypeApplied={selectedBType}
                    isSHouseApplied={isSHouse}
                    isDispositionedApplied={isDispositioned}
                    showDispositionedFilter={showDispositionedFilter}
                    syncDraftWithApplied={syncDraftWithApplied}
                  />
                ),
                resetFunc: resetFunc,
                applyFunc: applyFunc,
              }}
              pagination={{
                ...pagination,
                totalItems: buildingTotal,
                totalPages: Math.ceil(buildingTotal / pagination.rowsPerPage) || 0,
              }}
              onChangePagination={setPagination}
              sortOptions={sortOptions}
              onChangeSort={setSortOptions}
              onClickBuilding={onClickBuilding}
            />
          </CustomBox>
        </React.Fragment>
      ) : (
        <Loading size={'large'} />
      )}
    </DashboardLayoutContainer>
  );
};

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  * {
    box-sizing: border-box;
  }
`;

const CustomBox = styled.div`
  position: relative;
`;
